import React, { useCallback, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { AgGridReact } from "@ag-grid-community/react";
import { Box } from "@mui/material";

import {
  selectFoldersMatchingSearchKey,
  selectProjectsMatchingSearchKey,
  selectReportsMatchingSearchKey,
  useGetFoldersQuery,
  useGetProjectsQuery,
  useGetReportsQuery,
} from "fond/api";
import { SEARCH_KEY } from "fond/constants";
import SearchBar from "fond/projects/SearchBar";
import { Folder, Project, Report, Store } from "fond/types";
import { GridId } from "fond/types/grids";
import { useQueryParams } from "fond/utils/hooks";
import { AgGrid } from "fond/widgets";

import { EntityMenu } from "../ItemMenu/EntityMenu";
import useProjectsTable from "../useProjectsTable";

const SearchResultContainer: React.FC = () => {
  const gridRef = useRef<AgGridReact>(null);
  const [menuAnchor, setMenuAnchor] = useState<Element | undefined>(undefined);
  const [menuEntity, setMenuEntity] = useState<Folder | Project | Report | undefined>(undefined);

  const searchQuery = useQueryParams(SEARCH_KEY) ?? "";

  const { isFetching: isFoldersFetching, isLoading: isFoldersLoading } = useGetFoldersQuery(undefined);
  const { isFetching: isProjectsFetching, isLoading: isProjectsLoading } = useGetProjectsQuery(undefined);
  const { isFetching: isReportsFetching, isLoading: isReportsLoading } = useGetReportsQuery(undefined);
  const suggestedProjects = useSelector((store: Store) => selectProjectsMatchingSearchKey(store, searchQuery));
  const suggestedFolders = useSelector((store: Store) => selectFoldersMatchingSearchKey(store, searchQuery));
  const suggestedReports = useSelector((store: Store) => selectReportsMatchingSearchKey(store, searchQuery));

  /**
   * Callback function that handles opening the row menu
   */
  const openRowMenu = useCallback((e: React.MouseEvent<HTMLButtonElement, MouseEvent>, entity?: Folder | Project) => {
    setMenuAnchor(e.currentTarget);
    setMenuEntity(entity);
  }, []);

  /**
   * Callback function that handles closing the row menu
   */
  const closeRowMenu = () => setMenuAnchor(undefined);

  const { columns: gridColumns } = useProjectsTable({ openRowMenu });

  const handleOnFilterChange = (value: string) => {
    gridRef.current?.api?.setGridOption("quickFilterText", value);
  };

  const isLoading = isProjectsLoading || isFoldersLoading || isReportsLoading;
  const isFetching = isProjectsFetching || isFoldersFetching || isReportsFetching;

  return (
    <>
      <Box my={3}>
        <SearchBar onChange={handleOnFilterChange} placeholder="Filter results..." />
      </Box>
      <Box data-testid="search-results-all">
        <AgGrid
          id={GridId.SEARCH}
          ref={gridRef}
          gridOptions={{
            defaultColDef: {
              resizable: true,
              suppressMovable: true,
              sortable: true,
            },
            domLayout: "autoHeight",
            animateRows: true,
            suppressGroupRowsSticky: true,
            sideBar: false,
            rowGroupPanelShow: "never",
          }}
          rowData={isLoading ? null : [...suggestedFolders, ...suggestedProjects, ...suggestedReports]}
          columnDefs={gridColumns}
          loading={isFetching}
          size="large"
          variant="outlined"
        />
      </Box>
      {(menuAnchor || menuEntity) && <EntityMenu entity={menuEntity} anchorEl={menuAnchor} onMenuClose={closeRowMenu} source="search-results-list" />}
    </>
  );
};
export default SearchResultContainer;
